
import Layout from "../../layouts/Layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { CheckCircle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { ServiceLabel } from "@/types/services";


interface ServiceDetailProps {
  serviceId: string;
  icon: React.ComponentType<any>;
  color: string;
  bgColor: string;
  serviceData?: ServiceTranslation;
}

interface ServiceTranslation {
  title: string;
  description: string;
  labels?: ServiceLabel[]; 
  process: Array<{ title: string; description: string; icon: string }>;
  bestPractices: Array<{ title: string; description: string }>;
}

const ServiceDetailLayout= ({ 
  serviceId,
  icon: Icon, 
  color, 
  bgColor, 
  serviceData: apiServiceData,
}: ServiceDetailProps) => {
  const { t, language } = useLanguage();
  const isRTL = language === "ar";

  
  const servicesData = apiServiceData ? null : t('services' as any)

  if (!apiServiceData && (!servicesData || typeof servicesData !== 'object')) {
    console.error('Services data not found or invalid');
    return <div>Service translations not found</div>;
  }

  const rawServiceData = servicesData && Object.values(servicesData).find(
    (service: any) => service.id === serviceId
  );
  if (!apiServiceData && (!rawServiceData || typeof rawServiceData !== 'object')) {
    console.error('Service data not found for:', serviceId);
    return <div>Service not found</div>;
  }

  const hasValidStructure = (data: any): data is ServiceTranslation => {
    return data && 
      typeof data.title === 'string' &&
      typeof data.description === 'string' &&
      Array.isArray(data.process) &&
      Array.isArray(data.bestPractices);
  };

  if (!hasValidStructure(apiServiceData || rawServiceData)) {
    console.error('Service data structure is invalid for:', serviceId);
    return <div>Service data is invalid</div>;
  }

  const serviceData = apiServiceData || rawServiceData as ServiceTranslation;

  return (
    <Layout>
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-b from-white to-gray-50 dark:from-nexus-dark dark:to-nexus-dark-light">
        <div className="container">         
          <div className="w-full">
            <div className="flex items-center mb-6 flex-wrap">
              <div className={`w-16 h-16 rounded-lg ${bgColor} flex items-center justify-center me-4`}>
                <Icon className={`h-8 w-8 ${color}`} />
              </div>
              <div className="flex-1">
                {serviceData.labels && serviceData.labels.length > 0 && (
                  <div className="flex gap-2 mb-2 flex-wrap">
                    {serviceData.labels.map((label, labelIndex) => (
                      <Badge 
                        key={labelIndex}
                        variant={label === ServiceLabel.BASIC ? "default" : "secondary"}
                        className={
                          label === ServiceLabel.BASIC  ? "bg-green-100 text-green-800" : 
                          label === ServiceLabel.ADVANCED? "bg-orange-100 text-orange-800" :
                          label === ServiceLabel.PREMIUM ? "bg-purple-100 text-purple-800" :
                          "bg-blue-100 text-blue-800"
                        }
                      >
                        {t(`services_labels.${label.toLowerCase()}`)}
                      </Badge>
                    ))}
                  </div>
                )}
                <h1 className="text-4xl md:text-5xl font-bold text-nexus-gray dark:text-gray-200">{serviceData.title}</h1>
              </div>
            </div>
            
            <div className="mb-8 border-gray-300"
              style={{
                [isRTL ? 'borderRightWidth' : 'borderLeftWidth']: '4px',
                [isRTL ? 'paddingRight' : 'paddingLeft']: '1.5rem'
              }}
            >
              <p className="text-xl text-gray-600 dark:text-gray-200 leading-relaxed">
                {serviceData.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process Section */}
      <section className="py-16 bg-white">
        <div className="container">
          <h2 className="text-3xl font-bold text-nexus-gray mb-12">{t("service_details_ourProcess")}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {serviceData.process.map((step, index) => (
              <Card key={index} className="border border-gray-200">
                <CardHeader className="pb-2">
                  <div className="flex items-center mb-2">
                    <span className="text-2xl me-3">{step.icon}</span>
                    <CardTitle className="text-xl text-nexus-gray dark:text-gray-200">{step.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 dark:text-gray-200">
                    {t(step.description)}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do Best Section */}
      <section className="py-16 bg-gray-50 dark:bg-nexus-dark">
        <div className="container">
          <h2 className="text-3xl font-bold text-nexus-gray dark:text-gray-200 mb-12">{t("service_details_whatWeDoBest")}</h2>
          
          <div className="space-y-6">
            {serviceData.bestPractices.map((practice, index) => (
              <div key={index} className="flex items-start">
                <div className="flex-shrink-0 me-4 mt-1">
                  <div className="w-6 h-6 rounded bg-nexus-teal/10 flex items-center justify-center">
                    <CheckCircle className="h-4 w-4 text-nexus-teal" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-nexus-gray dark:text-gray-200 mb-2">{practice.title}</h3>
                  <p className="text-gray-600 dark:text-gray-200">{practice.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-nexus-gray text-white">
        <div className="container text-center">
          <h2 className="text-3xl font-bold mb-4">{t("service_details_readyToStart")}</h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            {t("service_details_contactUs")} {serviceData.title} {t("service_details_needs")}
          </p>
          <Button asChild className="bg-nexus-brand hover:bg-nexus-brand/90">
            <Link to="/contact">
              {t("service_details_getInTouch")}
            </Link>
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default ServiceDetailLayout;
